import { Box, Card, CardContent, CardHeader } from "@mui/material"
import { Eye } from "lucide-react"
import { type EmailLayoutType } from "@crm-src/lib/email-layouts"
import { toPreviewImageUrl } from "@crm-src/lib/public-url"
import { socialFooterLinksHtml } from "@crm-src/lib/email-social"

interface CampaignEmailPreviewProps {
  layout: EmailLayoutType | undefined
  subject?: string
  content: string
  // Tenant info, used to render the simple_text logo + footer chrome that
  // crm-api adds at send time (so the preview matches the sent email).
  tenant?: any
}

const FRAGMENT_LAYOUTS = new Set<EmailLayoutType>(["simple_text", "ceo_letter"])

// The CEO letter is the ONE layout that is not a centered 600px column: it must
// sit flush against the left edge, exactly like the sent email (crm-api's
// `ceo_letter` branch in campaign_email.html.erb).
const LEFT_ANCHORED_LAYOUTS = new Set<EmailLayoutType>(["ceo_letter"])

const CEO_GREETING = '<p style="margin:0 0 16px;text-align:left">Hi [First Name],</p>'

// crm-api adds an unsubscribe to EVERY marketing email at send (2026-10-02 —
// the CEO letter included). The fragment layouts' chrome and the builder layouts'
// appended footer are reproduced here, inert, so the preview never under-reports
// what the recipient gets. Mirrors campaign_email.html.erb (`ceo_letter` branch)
// and EmailCampaignMailer#tracking_footer_html.
const CEO_UNSUBSCRIBE =
  '<p style="margin:28px 0 0;font-size:12px;line-height:1.5;color:#a0aec0;text-align:left">' +
  'Don&#39;t want these emails? <span style="text-decoration:underline">Unsubscribe</span></p>'
const BUILDER_UNSUBSCRIBE =
  '<div style="text-align:center;padding:16px;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#888888">' +
  '<span style="text-decoration:underline">Unsubscribe</span></div>'

const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!))

// Re-anchor the asset URLs the builders emit (logo / uploaded images) so the
// preview isn't full of broken images. Handles BOTH shapes builders produce:
//   • root-relative — `/api/v1/image/<key>.png`, `/stock-images/x.jpg`
//   • host-absolute — `https://support.xopnetworks.com/api/v1/image/<key>.png`
// The absolute form is what the webinar/masthead builders bake in (via
// `toAbsoluteUrl`, correct for the SENT email — those images are CID-inlined at
// send). But `support.xopnetworks.com` does NOT serve uploaded `/api/v1/image/*`
// (only crm-api does), so in the on-screen preview that hero 404s. We strip any
// leading `scheme://host` and re-anchor the PATH through `toPreviewImageUrl`
// (→ dashboard same-origin for uploads, support host for bundled stock images).
// This only affects the preview render — the stored/sent HTML is untouched.
const anchorImages = (html: string) =>
  (html || "").replace(
    /(src|background)=("|')(?:https?:\/\/[^"'/]+)?(\/(?:api\/v1\/image|stock-images)[^"']*)\2/gi,
    (_m, attr, q, path) => `${attr}=${q}${toPreviewImageUrl(path)}${q}`,
  )

// Build the full preview body for a layout. simple_text / ceo_letter store only a
// body FRAGMENT — crm-api adds the logo, the personalized greeting, and the
// footer at send — so the preview reproduces that chrome here. The other layouts
// already store the COMPLETE email HTML, so it's used verbatim.
function buildPreviewBody(layout: EmailLayoutType | undefined, content: string, tenant: any): string {
  const body = anchorImages((content || "").trim())

  if (layout === "ceo_letter") {
    // FLUSH LEFT, never centered — mirrors crm-api's `ceo_letter` branch in
    // campaign_email.html.erb. The letter has to read like a 1:1 note typed in
    // Outlook, so it starts at the left edge instead of floating in a centered
    // column (see the `margin:0` on `.xop-pv-wrap` for this layout).
    return `<div style="padding:24px 20px;text-align:left">${CEO_GREETING}${body}${CEO_UNSUBSCRIBE}</div>`
  }

  if (layout === "simple_text") {
    const logo = tenant?.logo_url
      ? `<div style="padding:30px 30px 8px"><img src="${esc(toPreviewImageUrl(tenant.logo_url))}" alt="" style="width:190px;max-width:100%;display:block"></div>`
      : ""
    const f: string[] = []
    if (tenant?.name) f.push(`<p style="margin:0 0 6px;font-weight:600;color:#111827">${esc(tenant.name)}</p>`)
    if (tenant?.email) f.push(`<p style="margin:4px 0">Email: ${esc(tenant.email)}</p>`)
    if (tenant?.phone) f.push(`<p style="margin:4px 0">Phone: ${esc(tenant.phone)}</p>`)
    if (tenant?.website) f.push(`<p style="margin:4px 0">Web: ${esc(tenant.website)}</p>`)
    if (tenant?.address) f.push(`<p style="margin:8px 0 0">${esc(tenant.address)}</p>`)
    // The "follow us" block, in the same place and with the same renderer the
    // SENT email uses (crm-api's chrome calls the Ruby mirror of this with
    // theme: :light). It was missing here, so every fragment-layout campaign —
    // the webinar thank-you and survey mails among them — LOOKED like it had
    // no socials while the delivered email had them all along. A preview that
    // under-reports is worse than no preview: it gets "fixed" by adding a
    // second copy to the body.
    const socials = socialFooterLinksHtml({ theme: "light", marginStyle: "margin:18px 0 0 0;" })
    // Always rendered — the real chrome emits the socials + unsubscribe
    // regardless of which tenant fields happen to be filled in.
    const footer =
      `<div style="background:#f1f5f9;padding:28px;border-top:1px solid #e5e7eb;text-align:center;color:#6b7280;font-size:13px">` +
      `${f.join("")}${socials}` +
      `<p style="margin:16px 0 0;font-size:12px;color:#9ca3af">Unsubscribe</p></div>`
    return `${logo}<div style="padding:36px"><p style="margin:0 0 20px;font-weight:600">Hello [First Name],</p>${body}</div>${footer}`
  }

  // newsletter / webinar / case_study — already complete HTML; crm-api appends
  // only the unsubscribe footer (and the invisible open pixel).
  return `${body}${BUILDER_UNSUBSCRIBE}`
}

/**
 * Always-visible, read-only live preview of the email being composed. Renders the
 * full email (with the greeting/logo/footer chrome crm-api adds for the fragment
 * layouts) in a sandboxed, email-width iframe — no scripts, links inert. This is
 * the ONE preview; the per-builder inline previews were removed in favor of it.
 */
export default function CampaignEmailPreview({ layout, content, tenant }: CampaignEmailPreviewProps) {
  const isFragment = layout ? FRAGMENT_LAYOUTS.has(layout) : false
  const built = buildPreviewBody(layout, content, tenant)
  const placeholder =
    '<p style="color:#9ca3af;font-style:italic;padding:24px">Your email content will appear here as you edit.</p>'
  const hasContent = (content || "").trim().length > 0
  // Fragment layouts give the inner card a white background; complete-HTML
  // layouts bring their own.
  const innerBg = isFragment ? "#ffffff" : "transparent"
  // Every layout is a centered 600px column EXCEPT the CEO letter, which is
  // left-anchored so the preview matches the flush-left email crm-api sends.
  const wrapMargin = layout && LEFT_ANCHORED_LAYOUTS.has(layout) ? "0" : "0 auto"
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>html,body{margin:0;padding:0}body{background:#f3f4f6;font-family:Arial,Helvetica,sans-serif;color:#111827;line-height:1.5}.xop-pv-wrap{max-width:600px;margin:${wrapMargin};background:${innerBg}}img{max-width:100%;height:auto}a{color:#1d4ed8}</style></head><body><div class="xop-pv-wrap">${hasContent ? built : placeholder}</div></body></html>`

  return (
    <Card sx={{ position: { lg: "sticky" }, top: { lg: 116 }, alignSelf: "start", minWidth: 0 }}>
      <CardHeader
        sx={{ pb: 1 }}
        title={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Eye size={20} />
            Live Preview
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Box
          component="iframe"
          title="Email preview"
          srcDoc={srcDoc}
          sandbox="allow-same-origin"
          sx={{
            width: "100%",
            height: "calc(100vh - 220px)",
            minHeight: 480,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "#f3f4f6",
            display: "block",
          }}
        />
      </CardContent>
    </Card>
  )
}
